var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { CssBaseline, Toolbar as MuiToolbar, ThemeProvider, AppBar as MuiAppBar, styled } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { darkTheme, lightTheme } from './theme';
import { ContextualizedNavbarContent, MadboxNavbarContext } from './Navbar';
import { Router } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { SnackbarProvider } from 'notistack';
import { SnackbarStack } from './SnackbarStack';
import { Loading } from './Loading';
import { useContent } from './hooks/useContent';
import './assets/font.css';
import { Menu } from './Menu';
import { QueryClientProvider } from 'react-query';
import useLocalStorage from '@madbox/use-local-storage';
import { useCallback } from 'react';
import { UseFiltersContextWrapper } from '@madbox/use-filters';
import DefaultNotLoggedView from './DefaultNotLoggedView';
var FullContainer = styled('div')(function (_a) {
    var theme = _a.theme;
    return ({
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        display: 'flex',
    });
});
var AppContainer = styled('div')(function (_a) {
    var theme = _a.theme;
    return ({
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        overflow: 'auto',
    });
});
var MainContainer = styled('div')(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            overflow: 'auto',
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            paddingLeft: theme.spacing(4),
            paddingRight: theme.spacing(4)
        },
        _b[theme.breakpoints.down('md')] = {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
        },
        _b);
});
var AppBar = styled(MuiAppBar)(function (_a) {
    var theme = _a.theme;
    return ({
        borderBottomColor: theme.palette.border,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
    });
});
var Toolbar = styled(MuiToolbar)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {},
        _b[theme.breakpoints.up('md')] = {
            paddingLeft: theme.spacing(4),
            paddingRight: theme.spacing(4),
        },
        _b[theme.breakpoints.down('md')] = {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
        },
        _b);
});
/**
 * The template contains:
 * - AppBar (header that contains navigation and toolbar)
 * - Drawer (the collapsable left menu to navigate between services)
 * - MainContainer (that contains the application itself)
 *
 * If the user is not logged in and no redirection, it will display content of prop `notLoggedView` instead of children
 *
 * @param props
 * @returns
 */
var MadboxTemplateContent = React.memo(function (props) {
    var children = props.children, notLoggedView = props.notLoggedView, hideMenu = props.hideMenu, hideProfile = props.hideProfile;
    var _a = useState(false), open = _a[0], setOpen = _a[1];
    var loading = useSelector(function (state) { return state.session.loading && !state.session.logingIn; });
    var user = useSelector(function (state) { return state.session.profile; });
    if (loading) {
        return _jsx(Loading, {}, void 0);
    }
    if (!user) {
        return _jsx(_Fragment, { children: notLoggedView !== undefined ? notLoggedView : _jsx(DefaultNotLoggedView, {}, void 0) }, void 0);
    }
    return (_jsxs(FullContainer, { children: [!hideMenu ? _jsx(Menu, { open: open, setOpen: setOpen }, void 0) : null, _jsxs(AppContainer, { children: [_jsx(AppBar, __assign({ position: "static", color: "inherit" }, { children: _jsx(Toolbar, { children: _jsx(ContextualizedNavbarContent, { menuOpen: open, setMenuOpen: setOpen, user: hideProfile ? null : user }, void 0) }, void 0) }), void 0), _jsx(MainContainer, __assign({ id: "main-container" }, { children: children }), void 0)] }, void 0)] }, void 0));
});
export var MadboxAppContext = React.createContext({
    devMode: false,
    setDevMode: function () { return void 0; },
    themeMode: null,
    setThemeMode: function () { return void 0; },
});
/**
 * Creates contexts needed to make the template work.
 *
 * @param props
 * @returns
 */
var MadboxContextWrapper = function (props) {
    var themeMode = props.themeMode, setThemeMode = props.setThemeMode, allowDevMode = props.allowDevMode, children = props.children, version = props.version;
    var _a = useLocalStorage('devMode'), devMode = _a[0], setDevModeString = _a[1];
    var setDevMode = useCallback(function (mode) { return setDevModeString(mode.toString()); }, [setDevModeString]);
    var _b = useContent([]), navbarContent = _b[0], setNavbarContent = _b[1], removeNavbarContent = _b[2];
    var navbarContext = useMemo(function () { return ({
        content: navbarContent,
        setContent: setNavbarContent,
        removeContent: removeNavbarContent,
    }); }, [navbarContent, setNavbarContent, removeNavbarContent]);
    var madboxContext = useMemo(function () { return ({
        allowDevMode: allowDevMode,
        devMode: (allowDevMode && devMode === 'true') || false,
        setDevMode: setDevMode,
        themeMode: themeMode,
        setThemeMode: setThemeMode,
        version: version,
    }); }, [devMode, setDevMode, allowDevMode, themeMode, setThemeMode, version]);
    return (_jsx(MadboxAppContext.Provider, __assign({ value: madboxContext }, { children: _jsx(MadboxNavbarContext.Provider, __assign({ value: navbarContext }, { children: children }), void 0) }), void 0));
};
/**
 * This component contains the header toolbars and the left menu.
 *
 * It should wrap your application.
 *
 * @example
 * const { history, store } = init({
 *   reducersMap: {},
 *   sagas: [],
 * });
 *
 * ReactDOM.render(
 *   <MadboxTemplate history={history} store={store}>
 *     <App />
 *   </MadboxTemplate>,
 *   document.getElementById('root')
 * );
 * @param props
 * @returns
 */
export var MadboxTemplate = function (props) {
    var history = props.history, store = props.store, children = props.children, notLoggedView = props.notLoggedView, hideMenu = props.hideMenu, hideProfile = props.hideProfile, queryClient = props.queryClient, allowDevMode = props.allowDevMode, filterOptions = props.filterOptions, version = props.version;
    var _a = useLocalStorage('themeMode'), themeMode = _a[0], setThemeMode = _a[1];
    return (_jsx(ThemeProvider, __assign({ theme: themeMode === 'dark' ? darkTheme : lightTheme }, { children: _jsx(Provider, __assign({ store: store }, { children: _jsx(QueryClientProvider, __assign({ client: queryClient }, { children: _jsx(Router, __assign({ history: history }, { children: _jsxs(MadboxContextWrapper, __assign({ themeMode: themeMode, setThemeMode: setThemeMode, allowDevMode: allowDevMode, version: version || process.env.REACT_APP_VERSION }, { children: [_jsx(CssBaseline, {}, void 0), _jsx(UseFiltersContextWrapper, __assign({ options: filterOptions }, { children: _jsx(MadboxTemplateContent, __assign({ notLoggedView: notLoggedView, hideMenu: hideMenu, hideProfile: hideProfile }, { children: children }), void 0) }), void 0), _jsx(SnackbarProvider, __assign({ anchorOrigin: { horizontal: 'right', vertical: 'bottom' } }, { children: _jsx(SnackbarStack, {}, void 0) }), void 0)] }), void 0) }), void 0) }), void 0) }), void 0) }), void 0));
};
